import { FaTicket } from "react-icons/fa6";
import { FiClock, FiLock, FiMail } from "react-icons/fi";
import { LinkProps } from "react-router";
import { Avatar } from "~/components/Avatar";
import { PrimaryButton } from "~/components/Button";
import { Text } from "~/components/Text";
import { EventFragment, EventRequestState, EventType } from "~/__generated__/graphql/graphql";
import { centsToMoney } from "~/utils/money";
import { useRootLocale } from "~/root";
import {
  DEFAULT_LOCALE,
  getRegionalCopy,
  type SupportedLocale,
} from "~/utils/localization";
import { getDisplayedTicketPriceCents } from "./ticketVariations";
import { isTicketSalesAwaitingApproval } from "./ticketSalesAvailability";

type ActionPanelProps = {
  plan: EventFragment;
  isAttending: boolean;
  atCapacity: boolean;
  waitlistSize?: number;
  to: LinkProps["to"];
  onClick?: () => void;
  onSeeOtherPlans?: () => void;
  className?: string;
  showPrivateGuestList?: boolean;
  showExposedGuestList?: boolean;
  ticketVariationsEnabled?: boolean;
};

const FAKE_AVATAR_SOURCES = [
  "/images/fake-profiles/fake-profile-1.png",
  "/images/fake-profiles/fake-profile-2.png",
  "/images/fake-profiles/fake-profile-3.png",
  "/images/fake-profiles/fake-profile-4.png",
  "/images/fake-profiles/fake-profile-5.png",
  "/images/fake-profiles/fake-profile-6.png",
  "/images/fake-profiles/fake-profile-7.png",
];

type CopyIcon = "ticket" | "mail" | "clock" | "none";
type CopyMode =
  | "default"
  | "pending-approval"
  | "request-status"
  | "scheduled"
  | "waitlist"
  | "see-other-plans";

export type Copy = {
  icon: CopyIcon;
  label: string;
  sublabel: string;
  ctaLabel: string;
  disabled: boolean;
  mode: CopyMode;
};

const MAX_VISIBLE_AVATARS = 7;

export function UrgencyPanel({
  plan,
  isAttending,
  atCapacity,
  waitlistSize = 0,
  to,
  onClick,
  onSeeOtherPlans,
  className = "",
  showPrivateGuestList = false,
  showExposedGuestList = false,
  ticketVariationsEnabled = false,
}: ActionPanelProps) {
  const locale = useRootLocale();
  const copy = getActionCopy({
    plan,
    isAttending,
    atCapacity,
    locale,
    ticketVariationsEnabled,
  });
  const isTicketed = plan.type === EventType.Ticketed;
  const attendees = plan.attendees.edges;

  const seeOtherPlans = copy.mode === "see-other-plans";
  const effectiveTo = seeOtherPlans ? undefined : to;
  const effectiveOnClick = seeOtherPlans ? onSeeOtherPlans : onClick;

  const showWaitlistAvatars =
    copy.mode !== "request-status" && isTicketed && atCapacity && plan.waitlists && waitlistSize > 0;
  const showGoingAvatars =
    ((!isTicketed && !isAttending) || showExposedGuestList) &&
    attendees.length > 0;
  const showFakePrivateGuestList = showPrivateGuestList && !showWaitlistAvatars;

  const renderIcon = (size: number) => {
    switch (copy.icon) {
      case "ticket":
        return <FaTicket size={size} aria-hidden />;
      case "mail":
        return <FiMail size={size} aria-hidden />;
      case "clock":
        return <FiClock size={size} aria-hidden />;
      case "none":
        return undefined;
    }
  };

  return (
    <section
      className={`group rounded-lg bg-gradient-to-r from-butter-lime via-butter-cream to-butter-pink p-[1.5px] shadow-[0_2px_24px_-12px_rgba(243,86,79,0.45)] transition-all duration-300 hover:shadow-[0_8px_40px_-12px_rgba(243,86,79,0.65)] lg:hover:-translate-y-0.5 ${className}`}
    >
      <div className="rounded-[7px] bg-butter-cream-light px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Text variant="secondary" size="xl" weight="bold">
              {copy.label}
            </Text>
            {copy.sublabel && (
              <Text variant="secondary" size="sm" weight="semibold">
                {copy.sublabel}
              </Text>
            )}
          </div>
          {showFakePrivateGuestList && (
            <div className="flex items-center gap-2 py-1.5">
              <div className="flex items-center -space-x-1.5">
                {FAKE_AVATAR_SOURCES.slice(0, 6).map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    aria-hidden
                    className="h-6 w-6 flex-shrink-0 rounded-full border border-butter-cream-light object-cover blur-[2px]"
                  />
                ))}
              </div>
              <div className="flex min-w-0 items-center gap-1.5">
                <FiLock
                  size={12}
                  strokeWidth={2.5}
                  className="flex-shrink-0 text-butter-green"
                  aria-hidden
                />
                <Text
                  as="span"
                  variant="secondary"
                  size="sm"
                  className="truncate tracking-[-0.01em]"
                >
                  Private guest list
                </Text>
              </div>
            </div>
          )}
          {showWaitlistAvatars && (
            <div className="flex items-center gap-2 py-1.5">
              <div className="flex items-center -space-x-1.5">
                {FAKE_AVATAR_SOURCES.slice(0, 6).map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    aria-hidden
                    className="h-6 w-6 flex-shrink-0 rounded-full border border-butter-cream-light object-cover blur-[2px]"
                  />
                ))}
              </div>
              <Text
                as="span"
                variant="secondary"
                size="sm"
                className="truncate"
              >
                {waitlistSize === 1
                  ? "1 person waitlisted"
                  : `${waitlistSize} people waitlisted`}
              </Text>
            </div>
          )}
          {showGoingAvatars && !showWaitlistAvatars && (
            <div className="flex items-center gap-2 py-1.5">
              <div className="flex items-center -space-x-1.5">
                {attendees.slice(0, MAX_VISIBLE_AVATARS).map((edge) => (
                  <Avatar
                    key={edge.node.id}
                    user={edge.node.user}
                    size={28}
                    rounded
                    className="flex-shrink-0 border border-butter-cream-light"
                  />
                ))}
                {attendees.length > MAX_VISIBLE_AVATARS && (
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-butter-cream-light bg-butter-cream">
                    <Text as="span" variant="secondary" size="xs" weight="bold">
                      +{attendees.length - MAX_VISIBLE_AVATARS}
                    </Text>
                  </div>
                )}
              </div>
              <Text
                as="span"
                variant="secondary"
                size="sm"
                className="truncate"
              >
                {attendees.length === 1
                  ? "1 person going"
                  : `${attendees.length} people going`}
              </Text>
            </div>
          )}
          {effectiveOnClick && !copy.disabled ? (
            <PrimaryButton
              theme="red"
              onClick={effectiveOnClick}
              className="w-full whitespace-nowrap font-bold"
              leftIcon={renderIcon(18)}
            >
              {copy.ctaLabel}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              theme="red"
              to={copy.disabled ? undefined : effectiveTo}
              disabled={copy.disabled}
              className="w-full whitespace-nowrap font-bold"
              leftIcon={renderIcon(18)}
            >
              {copy.ctaLabel}
            </PrimaryButton>
          )}
        </div>
      </div>
    </section>
  );
}

export function getActionCopy({
  plan,
  isAttending,
  atCapacity,
  locale = DEFAULT_LOCALE,
  ticketVariationsEnabled = false,
}: Pick<
  ActionPanelProps,
  "plan" | "isAttending" | "atCapacity" | "ticketVariationsEnabled"
> & {
  locale?: SupportedLocale;
}): Copy {
  const isTicketed = plan.type === EventType.Ticketed;
  const priceCents = getDisplayedTicketPriceCents(plan, ticketVariationsEnabled);
  const price = priceCents === undefined ? null : centsToMoney(priceCents, "$", locale);
  const regionalCopy = getRegionalCopy(locale);

  if (plan.canceled) {
    return {
      icon: "ticket",
      label: `Event ${regionalCopy.canceled}`,
      sublabel: "This plan is no longer happening.",
      ctaLabel: regionalCopy.canceledTitle,
      disabled: true,
      mode: "default",
    };
  }

  if (plan.hasEnded) {
    return {
      icon: "ticket",
      label: "Event has ended",
      sublabel: "Catch the next one.",
      ctaLabel: "Ended",
      disabled: true,
      mode: "default",
    };
  }

  if (isTicketSalesAwaitingApproval(plan)) {
    return {
      icon: "ticket",
      label: "Tickets pending approval",
      sublabel: "Ticket sales will open once approved.",
      ctaLabel: "Pending approval",
      disabled: true,
      mode: "pending-approval",
    };
  }

  if (isAttending) {
    return {
      icon: "mail",
      label: "You're going",
      sublabel: "Open the app to chat with guests and see updates.",
      ctaLabel: "Open in app",
      disabled: false,
      mode: "default",
    };
  }

  if (isTicketed) {
    if (!plan.isPublished) {
      return {
        icon: "ticket",
        label: "Tickets going live soon",
        sublabel: "Check back once tickets go live.",
        ctaLabel: "Get tickets",
        disabled: true,
        mode: "scheduled",
      };
    }

    if (plan.approveGuests && plan.myRequest?.submittedAt) {
      let label = "Request sent";
      let sublabel = "View your request for the latest updates.";
      switch (plan.myRequest.state) {
        case EventRequestState.Pending:
          label = "Pending";
          sublabel = "Your request is with the host.";
          break;
        case EventRequestState.Approving:
          label = regionalCopy.finalizingPayment;
          sublabel = "The host approved your request. We’re securing your ticket now.";
          break;
        case EventRequestState.PendingWaitlist:
          label = "You're on the waitlist";
          break;
        case EventRequestState.InAcceptedWaitlist:
          label = `A ${regionalCopy.place} is available`;
          break;
        case EventRequestState.Declined:
        case EventRequestState.Expired:
          label = "This request wasn't approved";
          break;
      }
      return {
        icon: "clock",
        label,
        sublabel,
        ctaLabel: "View request",
        disabled: false,
        mode: "request-status",
      };
    }

    if (atCapacity) {
      if (plan.waitlists) {
        return {
          icon: "clock",
          label: "Sold out",
          sublabel: `Join the waitlist in case a ${regionalCopy.place} opens up.`,
          ctaLabel: "Join the waitlist",
          disabled: false,
          mode: "waitlist",
        };
      }
      return {
        icon: "none",
        label: "Sold out",
        sublabel: "This one's gone - but there's plenty more on.",
        ctaLabel: "See what else is on",
        disabled: false,
        mode: "see-other-plans",
      };
    }
    return {
      icon: "ticket",
      label: price ? `From ${price}` : "Tickets available",
      sublabel: "No app needed. Get your ticket in 2 mins.",
      ctaLabel: "Get tickets",
      disabled: false,
      mode: "default",
    };
  }

  if (atCapacity) {
    if (plan.waitlists) {
      const attendeeCount = plan.attendees.edges.length;
      return {
        icon: "clock",
        label:
          attendeeCount === 1
            ? "1 person going solo"
            : `${attendeeCount} people going solo`,
        sublabel: `Join the waitlist in case a ${regionalCopy.place} opens up.`,
        ctaLabel: "Join the waitlist",
        disabled: false,
        mode: "waitlist",
      };
    }
    return {
      icon: "none",
      label: "At capacity",
      sublabel: "This one's full - but there's plenty more on.",
      ctaLabel: "See what else is on",
      disabled: false,
      mode: "see-other-plans",
    };
  }

  const attendeeCount = plan.attendees.edges.length;
  if (attendeeCount > 0) {
    return {
      icon: "mail",
      label:
        attendeeCount === 1
          ? "1 person going solo. Join them."
          : `${attendeeCount} people going solo. Join them.`,
      sublabel: "",
      ctaLabel: "RSVP",
      disabled: false,
      mode: "default",
    };
  }

  return {
    icon: "mail",
    label: "RSVP in the app",
    sublabel: "Turn up solo, leave with new friends.",
    ctaLabel: "RSVP",
    disabled: false,
    mode: "default",
  };
}
